import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CreditCard, Plus, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { DialogTour, DialogTourButton, keepDialogOpenForTour } from "@/components/PageTour";

export const ITEM_LABEL = {
  furniture: "Furniture", linen_towels: "Linen & towels", kitchenware: "Kitchen & crockery", appliances: "Appliances",
  electronics: "TV & electronics", walls_floors: "Walls, floors & carpets", fixtures: "Fixtures & fittings",
  outdoor: "Outdoor & garden", other: "Other",
};

export function DamageFlagBadge({ flag, testId }) {
  if (!flag) return null;
  return (
    <span data-testid={testId} title={`${flag.count} previous damage report(s) · ≈ £${flag.total_cost} · matched on ${flag.matched_on.join(", ")}`}
      className="ml-2 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-white bg-red-600 px-1.5 py-0.5 rounded">
      <AlertTriangle className="w-3 h-3" /> damage ×{flag.count}
    </span>
  );
}

export function ChargeDepositDialog({ report, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState(report.estimated_cost || "");
  const [reason, setReason] = useState(report.description || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/damage-reports/${report.id}/deposit`).then((r) => setInfo(r.data)).catch(() => {}); }, [report.id]);
  const stripe = info?.method === "stripe_deposit";
  const over = stripe && Number(amount) > info.available;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/damage-reports/${report.id}/charge`, { amount: Number(amount), reason });
      toast.success(data.charge.method === "stripe_deposit" ? `£${data.charge.amount} captured from the Stripe deposit` : `£${data.charge.amount} logged as a manual deduction`);
      if (data.invoice?.ok) toast.success(`Invoice emailed to ${data.invoice.to}`);
      else if (data.invoice) toast.error(`Invoice not sent: ${data.invoice.error || "email failed"}`);
      onDone();
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="charge-deposit-dialog" aria-describedby={undefined} onInteractOutside={keepDialogOpenForTour}>
        <DialogTour open tourKey="/damage#charge" />
        <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
          <DialogTitle className="font-serif flex items-center gap-2"><CreditCard className="w-5 h-5 text-teal-700" /> Charge deposit</DialogTitle>
          <DialogTourButton tourKey="/damage#charge" />
        </DialogHeader>
        <div className="text-xs text-slate-500 -mt-2">{report.guest_name || "Guest"} · {(report.property_name || "").replace(/\*/g, " · ")} {report.stay}</div>
        {!info ? <div className="text-sm text-slate-400" data-testid="charge-deposit-method">Checking deposit…</div> : (
          <div data-testid="charge-deposit-method" className={`text-sm rounded-lg px-3 py-2 border ${stripe ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-amber-50 border-amber-100 text-amber-800"}`}>
            {stripe ? `Stripe deposit hold available: £${info.available.toFixed(2)} — the amount will be captured from the guest's card.` : info.why}
          </div>
        )}
        <Input type="number" min={0} step="0.01" placeholder="Amount £" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="charge-deposit-amount" />
        {over && <div className="text-xs text-rose-600" data-testid="charge-deposit-over">More than the available hold.</div>}
        <Textarea rows={3} placeholder="Reason (shown to the guest)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="charge-deposit-reason" />
        <Button className="w-full rounded-full ns-btn-primary" disabled={!info || busy || !(Number(amount) > 0) || over} onClick={submit} data-testid="charge-deposit-submit">
          {busy ? "Charging…" : stripe ? `Capture £${Number(amount || 0).toFixed(2)}` : `Log £${Number(amount || 0).toFixed(2)} deduction`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function PhotoRoomsEditor({ propertyId, canEdit }) {
  const [data, setData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/photo-rooms`).then((r) => { setData(r.data); setRooms(r.data.rooms); }).catch(() => {});
  }, [propertyId]);
  if (!propertyId || !data) return null;
  const save = async (list) => {
    try { const r = await api.put(`/properties/${propertyId}/photo-rooms`, { rooms: list }); setData(r.data); setRooms(r.data.rooms); toast.success("Photo checklist saved"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const add = () => { if (draft.trim()) { setRooms([...rooms, draft.trim()]); setDraft(""); } };
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid="photo-rooms-editor">
      <div className="text-sm font-medium">Photo check-out checklist</div>
      <p className="text-xs text-slate-500 mb-3">Cleaners must photograph each room before marking a turnover Done. {data.custom ? "Custom list." : "Pre-filled from bedrooms & bathrooms."}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {rooms.map((r, i) => (
          <span key={r + i} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-3 py-1" data-testid={`photo-room-${i}`}>
            {r}{canEdit && <button type="button" onClick={() => setRooms(rooms.filter((_, j) => j !== i))} data-testid={`photo-room-remove-${i}`} className="text-slate-400 hover:text-rose-600"><X className="w-3 h-3" /></button>}
          </span>
        ))}
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Input className="h-9 w-48 bg-white text-xs" placeholder="e.g. Utility room" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="photo-room-input" />
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={add} data-testid="photo-room-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          <Button type="button" size="sm" className="rounded-full ns-btn-primary" onClick={() => save(rooms)} data-testid="photo-rooms-save">Save checklist</Button>
          {data.custom && <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => save([])} data-testid="photo-rooms-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to suggested</Button>}
        </div>
      )}
    </div>
  );
}
